/* Shopify recommendations and multi-item cart additions; ordinary product forms stay native. */
if (!customElements.get('phq-complementary-products')) {
    customElements.define('phq-complementary-products', class extends HTMLElement {
        async connectedCallback() {
            if (this.ready) return;
            this.ready = true;
            this.form = this.closest('form');
            if (!this.form) return;
            this.controller = new AbortController();
            const { signal } = this.controller;
            try {
                const response = await fetch(this.dataset.url, { signal });
                if (!response.ok) return;
                const html = new DOMParser().parseFromString(await response.text(), 'text/html');
                const source = html.querySelector('phq-complementary-products');
                if (!this.isConnected || !source?.querySelector('[data-extra]')) return;
                this.innerHTML = source.innerHTML;
                this.hidden = false;
                this.button = this.form.querySelector('[name="add"]');
                this.defaultButtonText = this.button.textContent;
                this.payment = this.form.querySelector('.phq-product-accelerated-checkout');
                this.paymentWasHidden = this.payment?.hidden;
                this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
                    style: 'currency', currency: this.dataset.currency,
                });
                this.form.addEventListener('input', () => this.update(), { signal });
                this.form.addEventListener('change', (event) => {
                    if (event.target.matches('[data-extra-variant]')) {
                        const row = event.target.closest('[data-extra]');
                        const option = event.target.selectedOptions[0];
                        const check = row.querySelector('[data-extra-check]');
                        check.dataset.variantId = option.value;
                        check.dataset.price = option.dataset.price || '0';
                        check.disabled = !option.value;
                        if (check.disabled) check.checked = false;
                        if (option.dataset.money) {
                            const price = row.querySelector('[data-extra-price]');
                            price.firstChild.textContent = option.dataset.money + ' ';
                        }
                    }
                    this.update();
                }, { signal });
                this.querySelector('[data-more]')?.addEventListener('click', (event) => {
                    const button = event.currentTarget;
                    const expanded = button.getAttribute('aria-expanded') !== 'true';
                    this.querySelectorAll('[data-extra]').forEach((row, index) => {
                        if (index > 1) row.hidden = !expanded;
                    });
                    button.setAttribute('aria-expanded', String(expanded));
                    button.textContent = expanded ? button.dataset.lessLabel : button.dataset.moreLabel;
                }, { signal });
                this.form.addEventListener('submit', (event) => this.submit(event), { signal });
                this.update();
            } catch (error) {
                // Recommendations are optional; a failed lookup must not block the main purchase.
                if (error.name !== 'AbortError') this.hidden = true;
            }
        }

        disconnectedCallback() {
            this.controller?.abort();
            this.ready = false;
        }

        selected() {
            return [...this.querySelectorAll('[data-extra-check]:checked')].filter(check => check.dataset.variantId);
        }

        update() {
            if (this.busy) return;
            const selected = this.selected();
            const variant = this.form.querySelector('[name="id"]');
            const price = Number((variant.selectedOptions?.[0] || variant).dataset.price);
            const quantity = Number(this.form.querySelector('[name="quantity"]')?.value) || 1;
            const total = price * quantity + selected.reduce((sum, check) => sum + Number(check.dataset.price), 0);
            this.querySelector('[data-total-row]').hidden = selected.length === 0;
            this.querySelector('[data-total]').textContent = this.money.format(total / 100);
            this.button.textContent = selected.length ? this.dataset.basketLabel : this.defaultButtonText;
            if (this.payment) this.payment.hidden = selected.length > 0 || this.paymentWasHidden;
            this.querySelectorAll('[data-extra-check]').forEach(check => {
                const row = check.closest('[data-extra]');
                row.classList.toggle('is-selected', check.checked);
                const label = row.querySelector('[data-choice-label]');
                label.textContent = check.checked ? label.dataset.remove : label.dataset.add;
                check.setAttribute('aria-label', label.textContent + ' — ' + check.dataset.productTitle);
            });
        }

        async submit(event) {
            if (this.busy) { event.preventDefault(); return; }
            const selected = this.selected();
            if (!selected.length) return;
            event.preventDefault();
            const data = new FormData(this.form);
            const main = { id: data.get('id'), quantity: Number(data.get('quantity') || 1) };
            if (data.get('selling_plan')) main.selling_plan = data.get('selling_plan');
            const properties = {};
            for (const [key, value] of data) {
                const match = key.match(/^properties\[(.+)\]$/);
                if (match) properties[match[1]] = value;
            }
            if (Object.keys(properties).length) main.properties = properties;
            const items = [main, ...selected.map(check => ({ id: check.dataset.variantId, quantity: 1 }))];
            const controls = [...this.form.querySelectorAll('input, select, button')].map(control => [control, control.disabled]);
            this.busy = true;
            controls.forEach(([control]) => { control.disabled = true; });
            this.form.setAttribute('aria-busy', 'true');
            this.button.textContent = this.dataset.busyLabel;
            this.querySelector('[data-cart-error]').hidden = true;
            try {
                const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ items }),
                });
                if (!response.ok) throw new Error('Cart addition was not confirmed');
                // Match the native product form: show the actual cart, including any Shopify discounts.
                window.location.assign(this.dataset.cartUrl);
            } catch (_error) {
                // Do not retry automatically: Shopify may already have added some of the items.
                this.querySelector('[data-error-message]').textContent = this.dataset.errorLabel;
                this.querySelector('[data-cart-error]').hidden = false;
                const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js').catch(() => null);
                if (cartResponse?.ok) {
                    const cart = await cartResponse.json().catch(() => null);
                    const count = document.getElementById('cart-count');
                    if (cart && count) count.textContent = cart.item_count;
                }
            } finally {
                this.busy = false;
                controls.forEach(([control, disabled]) => { control.disabled = disabled; });
                this.form.removeAttribute('aria-busy');
                this.update();
            }
        }
    });
}
